import { type DashboardOverview } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import { Suspense } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { DonutShare, LineTrend } from '../../components/charts';
import { KpiCard } from '../../components/KpiCard';
import { PageHeader } from '../../components/PageHeader';
import { Pill } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { dashboardApi } from '../../lib/apiClient';
import { bangkokBusinessDay } from '../../lib/businessDate';
import { CHART_H } from '../../lib/chartTokens';
import { dayToIso } from '../../lib/dateRange';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtInt,
  fmtPct,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';

const STATUS_TONE = {
  online: 'ok',
  syncing: 'primary',
  offline: 'danger',
  unknown: 'neutral',
} as const;
const STATUS_TH = {
  online: 'ออนไลน์',
  syncing: 'กำลังซิงค์',
  offline: 'ออฟไลน์',
  unknown: 'ไม่ทราบ',
} as const;

const ALERT_CLASSES: Record<
  DashboardOverview['alerts'][number]['severity'],
  string
> = {
  danger: 'bg-danger-light/30 border-danger/15 hover:bg-danger-light/50',
  warn: 'bg-warn-light/30 border-warn/15 hover:bg-warn-light/50',
  info: 'bg-primary-light/30 border-primary/15 hover:bg-primary-light/50',
};

const ALERT_ICON_CLASSES: Record<
  DashboardOverview['alerts'][number]['severity'],
  string
> = {
  danger: 'bg-danger text-white',
  warn: 'bg-warn text-white',
  info: 'bg-primary text-white',
};

// Custom inline SVG icons for KPI Cards to prevent external package dependencies
const RevIcon = () => (
  <svg
    className="w-4.5 h-4.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const BillIcon = () => (
  <svg
    className="w-4.5 h-4.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" />
    <path d="M6 8h12M6 12h12M6 16h12" />
  </svg>
);

const AvgIcon = () => (
  <svg
    className="w-4.5 h-4.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const PayIcon = () => (
  <svg
    className="w-4.5 h-4.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <line x1="12" y1="4" x2="12" y2="20" />
  </svg>
);

export function DashboardView() {
  const { scope, ready } = useBranchScope();
  const { data, isLoading, isError, isFetching, dataUpdatedAt, refetch } =
    useQuery({
      queryKey: ['dashboard', scope.branchId, scope.allowedBranchIds],
      queryFn: () =>
        dashboardApi.getOverview({
          date: dayToIso(bangkokBusinessDay()),
          branchId: scope.branchId ?? undefined,
        }),
      enabled: ready,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
    });
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: () => dashboardApi.listBranches(),
  });
  const shortOf = (id: string) =>
    branches.find((b) => b.id === id)?.short ?? id;

  if (!ready || isLoading) {
    return (
      <div>
        <PageHeader title="ภาพรวม" subtitle="ยอดขายและกิจกรรมวันนี้" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bo-card h-[120px] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <PageHeader title="ภาพรวม" subtitle="ยอดขายและกิจกรรมวันนี้" />
        <div className="bo-card p-8 text-center space-y-4">
          <div className="font-bold text-danger">โหลดข้อมูลภาพรวมไม่สำเร็จ</div>
          <button
            className="bo-btn bo-btn-primary"
            onClick={() => void refetch()}
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  const {
    kpis,
    branches: branchToday,
    topProducts,
    recentReceipts,
    alerts,
  } = data;
  const chartSkeleton = (
    <div
      className="animate-pulse bg-bg-warm rounded-card"
      style={{ height: CHART_H.md }}
    />
  );
  const trend = data.trend.map((row) => ({
    label: row.date.slice(-2),
    value: row.amount,
  }));
  const paySplit = data.payments.map((row) => ({
    label: row.label,
    value: row.amount,
  }));
  return (
    <div className="bo-fade space-y-6">
      <PageHeader title="ภาพรวม" subtitle="ยอดขายและกิจกรรมวันนี้" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="text-sm-label text-ink-3">
          {isError
            ? 'อัปเดตไม่สำเร็จ กำลังแสดงข้อมูลครั้งล่าสุด'
            : `อัปเดตล่าสุด ${new Date(dataUpdatedAt).toLocaleTimeString('th-TH', { timeZone: 'Asia/Bangkok' })} · อัปเดตทุก 30 วินาที`}
        </p>
        <Btn disabled={isFetching} onClick={() => void refetch()}>
          {isFetching ? 'กำลังอัปเดต…' : 'อัปเดตข้อมูล'}
        </Btn>
      </div>
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="ยอดขาย"
          value={fmtAmount(kpis.revenue)}
          unit="บาท"
          delta={kpis.growth ?? undefined}
          sub={kpis.growth == null ? undefined : 'เทียบกับเมื่อวาน'}
          icon={<RevIcon />}
          tone="primary"
        />
        <KpiCard
          label="จำนวนบิล"
          value={fmtInt(kpis.bills)}
          unit="บิล"
          sub="ทำรายการสำเร็จวันนี้"
          icon={<BillIcon />}
          tone="accent"
        />
        <KpiCard
          label="เฉลี่ยต่อบิล"
          value={fmtAmount(kpis.averageBill)}
          unit="บาท"
          sub="มูลค่าเฉลี่ยต่อตะกร้าสินค้า"
          icon={<AvgIcon />}
          tone="ok"
        />
        <KpiCard
          label="สัดส่วนเงินสด/QR"
          value={
            kpis.cashShare === null || kpis.qrShare === null
              ? 'ยังไม่มีข้อมูล'
              : `${fmtPct(kpis.cashShare * 100)} / ${fmtPct(kpis.qrShare * 100)}`
          }
          sub="ช่องทางชำระเงินวันนี้"
          icon={<PayIcon />}
          tone="warn"
        />
      </div>

      {/* Trend + payment split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="แนวโน้มยอดขาย 14 วัน" className="lg:col-span-2 shadow-sm">
          <Suspense fallback={chartSkeleton}>
            <LineTrend
              data={trend}
              valueFormat={fmtBahtShort}
              tickFormat={fmtSatangCompact}
            />
          </Suspense>
        </Card>
        <Card title="สัดส่วนการชำระเงิน (บาท)" className="shadow-sm">
          <Suspense fallback={chartSkeleton}>
            <DonutShare data={paySplit} valueFormat={fmtBahtShort} />
          </Suspense>
        </Card>
      </div>

      {/* Main Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Branch Sales Performance Card */}
        <Card title="ยอดขายแยกสาขา" className="lg:col-span-2 shadow-sm">
          <DataTable
            pageSize={Number.MAX_SAFE_INTEGER}
            mobileCards
            data={branchToday}
            columns={[
              {
                id: 'branch',
                header: 'สาขา',
                cell: ({ row }) => shortOf(row.original.branchId),
              },
              {
                accessorKey: 'revenue',
                header: 'ยอดขาย',
                cell: ({ row }) => fmtAmount(row.original.revenue),
              },
              {
                accessorKey: 'bills',
                header: 'จำนวนบิล',
                cell: ({ row }) => fmtInt(row.original.bills),
              },
              {
                id: 'growth',
                header: 'Δ เทียบวันก่อน',
                cell: ({ row }) => {
                  const growth = row.original.growth;
                  if (growth == null) return '—';
                  return (
                    <span
                      className={`whitespace-nowrap font-semibold ${growth > 0 ? 'text-ok' : growth < 0 ? 'text-danger' : 'text-ink-3'}`}
                    >
                      {growth > 0 ? '▲ +' : growth < 0 ? '▼ ' : ''}
                      {fmtPct(growth * 100)}
                    </span>
                  );
                },
              },
              {
                id: 'status',
                header: 'สถานะเชื่อมต่อ',
                cell: ({ row }) => (
                  <Pill tone={STATUS_TONE[row.original.connectivity]}>
                    {STATUS_TH[row.original.connectivity]}
                  </Pill>
                ),
              },
            ]}
          />
        </Card>

        {/* Notifications & Alerts */}
        <Card title="แจ้งเตือน & ความปลอดภัย" className="shadow-sm">
          <ul className="flex flex-col gap-3">
            {alerts.length === 0 ? (
              <div className="text-center py-6 text-sm text-ink-4">
                ไม่มีรายการแจ้งเตือนในขณะนี้
              </div>
            ) : (
              alerts.map((a, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-3 p-3 rounded-btn border ${ALERT_CLASSES[a.severity]} transition-all duration-200 shadow-sm`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 shadow-sm ${ALERT_ICON_CLASSES[a.severity]}`}
                  >
                    !
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-body font-bold text-ink leading-snug">
                      {a.title}
                    </div>
                    <div className="text-sm-label text-ink-2 leading-relaxed">
                      {a.detail}
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </Card>
      </div>

      {/* Grid: Top Sellers & Recent Bills */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Sellers Table */}
        <Card title="สินค้าขายดีวันนี้" className="shadow-sm">
          <DataTable
            pageSize={Number.MAX_SAFE_INTEGER}
            mobileCards
            data={topProducts}
            columns={[
              { accessorKey: 'productName', header: 'สินค้า' },
              {
                accessorKey: 'quantity',
                header: 'จำนวนชิ้น',
                cell: ({ row }) => fmtInt(row.original.quantity),
              },
              {
                accessorKey: 'amount',
                header: 'ยอดขายรวม',
                cell: ({ row }) => fmtAmount(row.original.amount),
              },
            ]}
          />
        </Card>

        {/* Recent Bills Table */}
        <Card title="บิลล่าสุด" className="shadow-sm">
          <DataTable
            pageSize={Number.MAX_SAFE_INTEGER}
            mobileCards
            data={recentReceipts}
            columns={[
              { accessorKey: 'receiptNo', header: 'เลขบิล' },
              {
                id: 'payment',
                header: 'วิธีชำระเงิน',
                cell: ({ row }) =>
                  row.original.paymentMethods.length
                    ? row.original.paymentMethods
                        .map(
                          (method) =>
                            data.payments.find(
                              (payment) => payment.method === method,
                            )?.label ??
                            (
                              {
                                cash: 'เงินสด',
                                qr_personal: 'พร้อมเพย์',
                                qr_gateway: 'QR ผ่านผู้ให้บริการ',
                                reference_manual: 'ช่องทางรับเงินอื่น',
                              } as Record<string, string>
                            )[method] ??
                            'ช่องทางรับเงินอื่น',
                        )
                        .join(' + ')
                    : 'ไม่ระบุวิธีชำระ',
              },
              {
                accessorKey: 'totalAmount',
                header: 'ยอดชำระ',
                cell: ({ row }) => fmtBaht(row.original.totalAmount),
              },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}
